import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduledReportsManager = ({ onClose }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    reportType: '',
    frequency: '',
    recipients: '',
    format: 'pdf',
    includeCharts: true,
    includeTables: true
  });

  const scheduledReports = [
    {
      id: 1,
      name: 'Resumo Mensal Automático',
      reportType: 'Resumo Mensal',
      frequency: 'Mensal',
      nextRun: '01/12/2025',
      recipients: 'rita@jrwise.com.br, joao@jrwise.com.br',
      format: 'PDF',
      status: 'active',
      lastRun: '01/11/2025',
      lastStatus: 'success'
    },
    {
      id: 2,
      name: 'Análise Semanal de Fluxo de Caixa',
      reportType: 'Fluxo de Caixa',
      frequency: 'Semanal',
      nextRun: '02/12/2025',
      recipients: 'rita@jrwise.com.br',
      format: 'Excel',
      status: 'active',
      lastRun: '25/11/2025',
      lastStatus: 'success'
    },
    {
      id: 3,
      name: 'Relatório Trimestral Executivo',
      reportType: 'Revisão Trimestral',
      frequency: 'Trimestral',
      nextRun: '01/01/2026',
      recipients: 'rita@jrwise.com.br, joao@jrwise.com.br',
      format: 'PDF',
      status: 'active',
      lastRun: '01/10/2025',
      lastStatus: 'success'
    },
    {
      id: 4,
      name: 'Análise de Despesas Mensal',
      reportType: 'Detalhamento de Despesas',
      frequency: 'Mensal',
      nextRun: '01/12/2025',
      recipients: 'joao@jrwise.com.br',
      format: 'PDF',
      status: 'paused',
      lastRun: '01/11/2025',
      lastStatus: 'failed'
    }
  ];

  const reportTypeOptions = [
    { value: 'monthly-summary', label: 'Resumo Mensal' },
    { value: 'client-profitability', label: 'Lucratividade por Cliente' },
    { value: 'expense-breakdown', label: 'Detalhamento de Despesas' },
    { value: 'cash-flow', label: 'Fluxo de Caixa' },
    { value: 'quarterly-review', label: 'Revisão Trimestral' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Diário' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensal' },
    { value: 'quarterly', label: 'Trimestral' },
    { value: 'yearly', label: 'Anual' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Creating scheduled report:', formData);
    setIsCreating(false);
    setFormData({
      name: '',
      reportType: '',
      frequency: '',
      recipients: '',
      format: 'pdf',
      includeCharts: true,
      includeTables: true
    });
  };

  const handleToggleStatus = (reportId) => {
    console.log('Toggling status for report:', reportId);
  };

  const handleDelete = (reportId) => {
    if (window.confirm('Tem certeza que deseja excluir este agendamento?')) {
      console.log('Deleting scheduled report:', reportId);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Ativo', color: 'bg-success/10 text-success', icon: 'CheckCircle' },
      paused: { label: 'Pausado', color: 'bg-warning/10 text-warning', icon: 'PauseCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getLastStatusIcon = (status) => {
    return status === 'success' ? (
      <Icon name="CheckCircle" size={16} className="text-success" />
    ) : (
      <Icon name="XCircle" size={16} className="text-error" />
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1030] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Relatórios Agendados</h2>
            <p className="text-sm text-muted-foreground mt-1">Gerencie relatórios automáticos e agendamentos</p>
          </div>
          <div className="flex items-center gap-2">
            {!isCreating && (
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsCreating(true)}
              >
                Novo Agendamento
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-muted transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isCreating ? (
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              <div className="bg-background border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Novo Agendamento</h3>
                
                <div className="space-y-4">
                  <Input
                    label="Nome do Agendamento"
                    type="text"
                    placeholder="Ex: Resumo Mensal Automático"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    required
                  />

                  <Select
                    label="Tipo de Relatório"
                    options={reportTypeOptions}
                    value={formData?.reportType}
                    onChange={(value) => handleInputChange('reportType', value)}
                    required
                  />

                  <Select
                    label="Frequência"
                    options={frequencyOptions}
                    value={formData?.frequency}
                    onChange={(value) => handleInputChange('frequency', value)}
                    required
                  />

                  <Input
                    label="Destinatários"
                    type="text"
                    placeholder="email1@exemplo.com, email2@exemplo.com"
                    description="Separe múltiplos emails com vírgula"
                    value={formData?.recipients}
                    onChange={(e) => handleInputChange('recipients', e?.target?.value)}
                    required
                  />

                  <Select
                    label="Formato de Exportação"
                    options={formatOptions}
                    value={formData?.format}
                    onChange={(value) => handleInputChange('format', value)}
                    required
                  />

                  <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium text-foreground mb-2">Opções do Relatório</p>
                    <Checkbox
                      label="Incluir Gráficos"
                      checked={formData?.includeCharts}
                      onChange={(e) => handleInputChange('includeCharts', e?.target?.checked)}
                    />
                    <Checkbox
                      label="Incluir Tabelas Detalhadas"
                      checked={formData?.includeTables}
                      onChange={(e) => handleInputChange('includeTables', e?.target?.checked)}
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => setIsCreating(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" fullWidth>
                    Criar Agendamento
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {scheduledReports?.map((report) => (
                <div
                  key={report?.id}
                  className="bg-background border border-border rounded-lg p-6 hover:border-primary/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-base font-semibold text-foreground">{report?.name}</h3>
                        {getStatusBadge(report?.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{report?.reportType}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(report?.id)}
                        className="p-2 rounded-md hover:bg-muted transition-colors"
                        title={report?.status === 'active' ? 'Pausar' : 'Ativar'}
                      >
                        <Icon name={report?.status === 'active' ? 'Pause' : 'Play'} size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(report?.id)}
                        className="p-2 rounded-md hover:bg-error/10 text-error transition-colors"
                        title="Excluir"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Frequência</p>
                      <p className="text-sm font-medium text-foreground">{report?.frequency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Próxima Execução</p>
                      <p className="text-sm font-medium text-foreground">{report?.nextRun}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Formato</p>
                      <p className="text-sm font-medium text-foreground">{report?.format}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Última Execução</p>
                      <div className="flex items-center gap-2">
                        {getLastStatusIcon(report?.lastStatus)}
                        <p className="text-sm font-medium text-foreground">{report?.lastRun}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Destinatários</p>
                    <p className="text-sm text-foreground">{report?.recipients}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduledReportsManager;